import React, { useMemo } from 'react';
import { OrganizationsProvider, useOrganizationsContext } from './OrganizationsProvider';
import { DefaultHeader } from '../UI/V3/DefaultHeader/DefaultHeader';
import { SelectionBar } from '../UI/V3/SelectionBar/SelectionBar';
import SearchBar from '../UI/V3/SearchBar/SearchBar';
import Button from '../UI/V3/Button/Button';
import { faPlus, faBuilding } from '@fortawesome/pro-solid-svg-icons';
import AddOrganizationModal from './Add/AddOrganizationModal';

import OrganizationsTable from './List/OrganizationsTable';
import { Organization } from 'model/organization';
import Spinner from 'components/UI/V3/Spinner';
import EmptyState from '../common/EmptyState';
import BusinessTypeFilterDropdown from './BusinessTypeFilterDropdown';
import styles from './OrganizationsComponent.module.scss';
import { ConfirmDialog } from 'components/UI';
import RootOrganization from './RootOrganization/RootOrganization';
import { useMediaQuery } from 'react-responsive';
import { OrganizationStatus } from 'reducers/organizations/OrganizationState';

const OrganizationsContent: React.FC = () => {
    const {
        // Data
        loading,
        status,
        organizationsCount,

        // Filter state
        selectedOption,
        searchText,

        //Parent accounts
        parentAccounts,

        // Permissions
        canAddAccounts,

        // Handlers
        handleStatusChange,
        handleSearchChange,
        handleAddAccount,

        //toggle status
        showConfirm,
        setShowConfirm,
        handleActivateAccount,
        handleDeactivateAccount,
        handleDeleteAccount
    } = useOrganizationsContext();

    const [parentOrganization, setParentOrganization] = React.useState<Organization | null>(null);

    const isMobile = useMediaQuery({ query: '(max-width:768px)' });

    const showFilter = false;

    React.useEffect(() => {
        if (parentAccounts.length > 0) {
            setParentOrganization(parentAccounts[0]);
        }
    }, [parentAccounts]);

    // Memoize static options array
    const selectionBarOptions = useMemo(
        () => [
            { id: 'active', name: 'Active' },
            { id: 'inactive', name: 'Inactive', selectedBackgroundColor: '#000' },
            { id: 'all', name: 'All', selectedBackgroundColor: '#000' }
        ],
        []
    );

    const renderContent = () => {
        // Initial loading
        if (loading && status === OrganizationStatus.Initial) {
            return (
                <div className={styles.loadingContainer}>
                    <Spinner size={32} />
                </div>
            );
        }

        // Empty state (no organizations fetched)
        if (organizationsCount === 0 && status === OrganizationStatus.FetchSuccess) {
            return (
                <div className={styles.root}>
                    <div className={styles.headWrapper}>
                        <DefaultHeader
                            title="Organizations"
                            textBelowTitle="Manage organizations and their details."
                            displayButton={false}
                            className={styles.headerContainer}
                        />
                    </div>
                    <EmptyState
                        icon={faBuilding}
                        title="You have no organizations added yet"
                        description='Click the "Add organizations" button to add one.'
                        buttonLabel="Add organization"
                        buttonIcon={faPlus}
                        onButtonClick={handleAddAccount}
                        showButton={canAddAccounts}
                    />
                </div>
            );
        }

        // Has accounts - show full layout
        return (
            <div className={styles.root}>
                {/* Header with filters */}
                <div className={styles.headWrapper}>
                    <ConfirmDialog
                        open={showConfirm.show}
                        onCancel={() => setShowConfirm({ show: false, action: '', account: null })}
                        title={
                            showConfirm.action === 'activate'
                                ? 'Activate organization'
                                : showConfirm.action === 'delete'
                                ? 'Delete organization'
                                : 'Deactivate organization'
                        }
                        question={
                            showConfirm.action === 'activate'
                                ? 'Are you sure you want to activate this organization?'
                                : showConfirm.action === 'delete'
                                ? 'Are you sure you want to delete this organization? This action cannot be undone.'
                                : 'Are you sure you want to deactivate this organizations?'
                        }
                        actions={
                            <>
                                <Button
                                    fullWidth
                                    size="large"
                                    variant="black"
                                    label="Cancel"
                                    style={{ minWidth: 'unset' }}
                                    onClick={() => setShowConfirm({ show: false, action: '', account: null })}
                                />

                                <Button
                                    fullWidth
                                    size="large"
                                    variant="danger"
                                    label={
                                        showConfirm.action === 'activate'
                                            ? 'Yes, Activate'
                                            : showConfirm.action === 'delete'
                                            ? 'Yes, Delete'
                                            : 'Yes, Deactivate'
                                    }
                                    style={{ margin: 0, minWidth: 'unset' }}
                                    onClick={() => {
                                        if (showConfirm.action === 'activate') {
                                            handleActivateAccount(showConfirm.account!);
                                        } else if (showConfirm.action === 'delete') {
                                            handleDeleteAccount(showConfirm.account!);
                                        } else {
                                            handleDeactivateAccount(showConfirm.account!);
                                        }
                                    }}
                                />
                            </>
                        }
                    />
                    <DefaultHeader
                        title="Organizations"
                        textBelowTitle="Manage organizations and their details."
                        displayButton={canAddAccounts}
                        buttonIcon={faPlus}
                        className={styles.headerContainer}
                        customButton={
                            <Button
                                label={isMobile ? 'Add' : 'Add organization'}
                                onClick={handleAddAccount}
                                variant="outlined"
                            />
                        }
                    />
                    {parentOrganization?.id && (
                        <RootOrganization
                            orgName={parentOrganization.name}
                            orgType={'Parent'}
                            orgAddress={`${parentOrganization.addressLineOne || ''} ${
                                parentOrganization.addressLineTwo || ''
                            }, ${parentOrganization.city || ''}, ${parentOrganization.state || ''}`}
                            orgPhone={parentOrganization.phone || ''}
                            orgEmail={parentOrganization.email || ''}
                        />
                    )}

                    {showFilter && (
                        <SelectionBar
                            options={selectionBarOptions}
                            selectedId={selectedOption}
                            onSelect={handleStatusChange}
                            className={styles.selectionBar}
                        />
                    )}

                    <SearchBar
                        searchText={searchText}
                        onChangeSearchText={handleSearchChange}
                        placeholder="Search by account"
                        className={styles.searchBarContainer}
                        rightActions={<BusinessTypeFilterDropdown />}
                    />
                </div>

                {/* Accounts Table */}
                <div className={styles.tableContainer}>
                    <OrganizationsTable />
                </div>
            </div>
        );
    };

    return (
        <div className={styles.accountsComponent}>
            {renderContent()}

            {/* Add/Edit Account Modal */}
            <AddOrganizationModal />
        </div>
    );
};

export const OrganizationsComponent: React.FC = () => {
    return (
        <OrganizationsProvider>
            <OrganizationsContent />
        </OrganizationsProvider>
    );
};

export default OrganizationsComponent;
